import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { getProjectAuthClaims, signInWithProjectToken } from '../lib/projectAuth';

export interface CustomerSessionUser {
  email: string;
  jiraKey?: string;
  role?: 'customer' | 'admin';
}

export const useSecureAuth = () => {
  const [user, setUser] = useState<CustomerSessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        localStorage.removeItem('userSession');
        setUser(null);
        setSessionExpired(true);
        setLoading(false);
        return;
      }

      const claims = await getProjectAuthClaims(firebaseUser);
      if (!claims || (claims.role !== 'customer' && claims.role !== 'admin')) {
        localStorage.removeItem('userSession');
        setUser(null);
        setSessionExpired(true);
        setLoading(false);
        return;
      }

      const sessionUser: CustomerSessionUser = {
        email: claims.email || firebaseUser.email || '',
        jiraKey: claims.jiraKey,
        role: claims.role,
      };

      setUser(sessionUser);
      setSessionExpired(false);
      setLoading(false);
      localStorage.setItem('userSession', JSON.stringify(sessionUser));
    });

    return () => unsubscribe();
  }, []);

  const signOut = async () => {
    try {
      localStorage.removeItem('userSession');
      await firebaseSignOut(auth);
      setUser(null);
      setSessionExpired(true);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const createSession = async (email: string, jiraKey: string, customToken?: string) => {
    try {
      if (!customToken) {
        throw new Error('Missing authentication token');
      }

      await signInWithProjectToken(customToken);

      const sessionUser: CustomerSessionUser = { email, jiraKey };
      setUser(sessionUser);
      setSessionExpired(false);
      setLoading(false);
      localStorage.setItem('userSession', JSON.stringify(sessionUser));
    } catch (error) {
      console.error('Error creating user session:', error);
      setSessionExpired(true);
      throw error;
    }
  };

  return {
    user,
    loading,
    sessionExpired,
    signOut,
    createSession,
  };
};
